import React, { useEffect } from 'react';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const TermsOfServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO 
        title="Terms of Service | Fearless IT"
        description="Read the terms and conditions governing the use of Fearless IT services."
      />
      <div className="min-h-screen bg-white">
        <MarketingHeader />
        
        {/* Hero Section */}
        <section className="pt-12 pb-6 sm:pt-16 sm:pb-8 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-black mb-4">
              Terms of Service
            </h1>
            <p className="text-slate-gray">Last updated: January 2025</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">1. Agreement to Terms</h2>
              <p className="text-slate-gray mb-4">
                By accessing or using the services provided by Fearless IT (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;), you agree to be bound by these Terms of Service (&ldquo;Terms&rdquo;). If you do not agree to these Terms, please do not use our services.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">2. Description of Services</h2>
              <p className="text-slate-gray mb-4">
                Fearless IT provides technology consulting, software development, and related services including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li>Custom SaaS application development</li>
                <li>Web and mobile application development</li>
                <li>Cloud infrastructure setup and management</li>
                <li>Technical consulting and strategy</li>
                <li>Ongoing support and maintenance services</li>
                <li>Hosting and deployment services</li>
              </ul>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">3. User Accounts</h2>
              <p className="text-slate-gray mb-4">
                To access certain features of our services, you may be required to create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Promptly update any information to keep it accurate</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">4. Project Engagements</h2>
              <h3 className="text-xl font-semibold text-carbon-black mt-6 mb-3">4.1 Project Proposals</h3>
              <p className="text-slate-gray mb-4">
                All project engagements will be governed by a separate Statement of Work (SOW) or project agreement that outlines specific deliverables, timelines, and pricing. These Terms serve as the master agreement governing all such engagements.
              </p>

              <h3 className="text-xl font-semibold text-carbon-black mt-6 mb-3">4.2 Payment Terms</h3>
              <p className="text-slate-gray mb-4">
                Payment terms will be specified in individual project agreements. Generally:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li>Project deposits are required before work commences</li>
                <li>Milestone payments are due upon delivery of agreed milestones</li>
                <li>Monthly retainers are billed in advance</li>
                <li>Late payments may incur interest charges</li>
              </ul>

              <h3 className="text-xl font-semibold text-carbon-black mt-6 mb-3">4.3 Changes and Scope</h3>
              <p className="text-slate-gray mb-4">
                Any changes to project scope must be documented in writing and may result in adjusted timelines and costs. We will provide estimates for any scope changes before proceeding.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">5. Intellectual Property</h2>
              <h3 className="text-xl font-semibold text-carbon-black mt-6 mb-3">5.1 Client Ownership</h3>
              <p className="text-slate-gray mb-4">
                Upon full payment, you will own all custom code, designs, and materials created specifically for your project, except for:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li>Pre-existing materials and frameworks owned by Fearless IT</li>
                <li>Third-party libraries and components</li>
                <li>General knowledge, skills, and techniques</li>
              </ul>

              <h3 className="text-xl font-semibold text-carbon-black mt-6 mb-3">5.2 License to Pre-existing Materials</h3>
              <p className="text-slate-gray mb-4">
                We grant you a non-exclusive, perpetual license to use any pre-existing materials incorporated into your deliverables for the purpose of operating your project.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">6. Confidentiality</h2>
              <p className="text-slate-gray mb-4">
                Both parties agree to maintain the confidentiality of any proprietary or sensitive information shared during the course of our engagement. This includes but is not limited to business plans, technical specifications, and financial information.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">7. Warranties and Disclaimers</h2>
              <h3 className="text-xl font-semibold text-carbon-black mt-6 mb-3">7.1 Service Warranty</h3>
              <p className="text-slate-gray mb-4">
                We warrant that our services will be performed in a professional and workmanlike manner consistent with industry standards.
              </p>

              <h3 className="text-xl font-semibold text-carbon-black mt-6 mb-3">7.2 Disclaimer</h3>
              <p className="text-slate-gray mb-4 uppercase font-semibold">
                EXCEPT AS EXPRESSLY PROVIDED HEREIN, OUR SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">8. Limitation of Liability</h2>
              <p className="text-slate-gray mb-4">
                To the maximum extent permitted by law, Fearless IT shall not be liable for any indirect, incidental, special, consequential, or punitive damages, regardless of the cause of action or whether we have been advised of the possibility of such damages.
              </p>
              <p className="text-slate-gray mb-4">
                Our total liability for any claims arising from these Terms or our services shall not exceed the total amount paid by you to us in the twelve (12) months preceding the claim.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">9. Indemnification</h2>
              <p className="text-slate-gray mb-4">
                You agree to indemnify and hold harmless Fearless IT and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of our services or violation of these Terms.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">10. Termination</h2>
              <p className="text-slate-gray mb-4">
                Either party may terminate a project engagement:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li>With 30 days written notice for convenience</li>
                <li>Immediately for material breach that remains uncured after 14 days notice</li>
                <li>Immediately if the other party becomes insolvent or bankrupt</li>
              </ul>
              <p className="text-slate-gray mb-4">
                Upon termination, you will pay for all work completed up to the termination date, and we will deliver all completed work product.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">11. Dispute Resolution</h2>
              <p className="text-slate-gray mb-4">
                Any disputes arising from these Terms shall first be attempted to be resolved through good-faith negotiation. If negotiation fails, disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">12. General Provisions</h2>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between the parties.</li>
                <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in effect.</li>
                <li><strong>Waiver:</strong> Failure to enforce any right does not constitute a waiver of that right.</li>
                <li><strong>Assignment:</strong> You may not assign these Terms without our written consent.</li>
                <li><strong>Governing Law:</strong> These Terms are governed by the laws of the United States.</li>
              </ul>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">13. Changes to Terms</h2>
              <p className="text-slate-gray mb-4">
                We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on our website. Your continued use of our services after such changes constitutes acceptance of the new Terms.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">14. Contact Information</h2>
              <p className="text-slate-gray mb-4">
                For questions about these Terms, please contact us at:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-carbon-black font-semibold">Fearless IT</p>
                <p className="text-slate-gray">Email: legal@fearlessit.io</p>
                <p className="text-slate-gray">Website: https://fearlessit.io</p>
              </div>

            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
};

export default TermsOfServicePage;
